import apiService from '../api'

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface ClearingDocumentApi {
  id: string
  name: string
  url: string
  type: string | null
}

export interface ClearingRecordApi {
  id: string
  shipmentId: string
  lotNumber?: string | null
  invoice?: string | null
  entryNumber?: string | null
  vat404ReceiptNo?: string | null
  vat404ReceiptStatus?: string | null
  status: string
  port?: string | null
  clearingAgent?: string | null
  clearingCost?: string | number | null
  notes?: string | null
  createdAt: string
  updatedAt: string
  shipment?: {
    id: string
    booking?: string | null
    buyContract?: string | null
    saleContract?: string | null
    bl?: string | null
    containerNumber?: string | null
    carrier?: string | null
    pol?: string | null
    pod?: string | null
    eta?: string | null
    shipmentContainers?: { containerNumber?: string | null }[] | null
  } | null
  documents?: ClearingDocumentApi[]
}

export interface ClearingListResponse {
  clearingRecords: ClearingRecordApi[]
  meta: PaginationMeta
}

export type ClearingPendingUploadType = 'shipping' | 'clearing' | 'bank'

export interface ClearingPendingUploadItem {
  id: string
  text: string
  clearingId: string
  clearingRef: string
  type: ClearingPendingUploadType
}

export interface ClearingPendingUploadsResponse {
  items: ClearingPendingUploadItem[]
  totalPending: number
  recordsAffected: number
}

export async function getClearingRecords(params?: {
  page?: number
  limit?: number
  status?: string
  containerNumber?: string
  search?: string
}): Promise<ClearingListResponse> {
  const qs = params ? new URLSearchParams(params as Record<string, string>).toString() : ''
  const res = await apiService.get<{ success: boolean; data: ClearingListResponse }>(
    `/clearing${qs ? '?' + qs : ''}`
  )
  const data = (res as any)?.data ?? res
  return {
    clearingRecords: data.clearingRecords ?? [],
    meta: data.meta ?? { page: 1, limit: params?.limit ?? 10, total: 0, totalPages: 0 },
  }
}

export async function getClearingPendingUploadsSummary(params?: {
  status?: string
  containerNumber?: string
  search?: string
}): Promise<ClearingPendingUploadsResponse> {
  const queryParams: Record<string, string> = {}
  if (params?.status) queryParams.status = params.status
  if (params?.containerNumber) queryParams.containerNumber = params.containerNumber
  if (params?.search) queryParams.search = params.search

  const qs = Object.keys(queryParams).length
    ? new URLSearchParams(queryParams).toString()
    : ''

  const res = await apiService.get<{ success: boolean; data: ClearingPendingUploadsResponse }>(
    `/clearing/pending-uploads${qs ? '?' + qs : ''}`
  )
  const data = (res as any)?.data ?? res
  return {
    items: data.items ?? [],
    totalPending: data.totalPending ?? 0,
    recordsAffected: data.recordsAffected ?? 0,
  }
}

/** Create clearing records for every shipment that doesn't have one. Returns created count and total shipments. */
export async function syncClearingFromShipments(): Promise<{ created: number; totalShipments: number }> {
  const res = await apiService.post<{
    success: boolean
    data: { created: number; totalShipments: number }
  }>('/clearing/sync-from-shipments')
  const data = (res as any)?.data ?? res
  return {
    created: data?.created ?? 0,
    totalShipments: data?.totalShipments ?? 0,
  }
}

export interface CreateClearingRecordPayload {
  shipmentId: string
  lotNumber?: string
  status?: string
  vat404ReceiptStatus?: string
  notes?: string
}

export async function createClearingRecord(
  payload: CreateClearingRecordPayload
): Promise<ClearingRecordApi> {
  const res = await apiService.post<{ success: boolean; data: ClearingRecordApi }>(
    '/clearing',
    payload
  )
  const data = (res as any)?.data ?? res
  return data
}

export async function updateClearingRecord(
  id: string,
  payload: Partial<{
    status: string
    vat404ReceiptStatus: string
    clearingAgent: string
    clearingCost: string | number
    notes: string
  }>
): Promise<ClearingRecordApi> {
  const res = await apiService.put<{ success: boolean; data: ClearingRecordApi }>(
    `/clearing/${id}`,
    payload
  )
  const data = (res as any)?.data ?? res
  return data
}

export interface UploadClearingDocumentResponse {
  fileUrl: string
}

export async function uploadClearingDocument(
  clearingRecordId: string,
  file: File,
  type?: 'shipping' | 'clearing' | 'bank'
): Promise<UploadClearingDocumentResponse> {
  const formData = new FormData()
  formData.append('file', file)
  if (type) formData.append('type', type)
  const res = await apiService.instance.post<{ success?: boolean; data?: UploadClearingDocumentResponse }>(
    `/clearing/${clearingRecordId}/documents`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  const body = (res as any)?.data
  const payload = body?.data ?? body
  return { fileUrl: payload?.fileUrl ?? '' }
}

/** Upload multiple files to a clearing record (one request per file). */
export async function uploadClearingDocuments(
  clearingRecordId: string,
  files: File[],
  type?: 'shipping' | 'clearing' | 'bank'
): Promise<void> {
  for (const file of files) {
    await uploadClearingDocument(clearingRecordId, file, type)
  }
}

/** Base URL for API (no trailing slash). Used to build view URLs that open in a new tab. */
const getApiBase = () => import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'

/** URL to view a clearing document in the browser (opens in new tab). */
export function getClearingDocumentViewUrl(clearingRecordId: string, documentId: string): string {
  const base = getApiBase().replace(/\/$/, '')
  return `${base}/clearing/${clearingRecordId}/documents/${documentId}/view`
}

